import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import styled, { useTheme } from "styled-components";
import { useNavigate, useParams } from "react-router";
import { Typography } from "ui-kit/Typography";
import { UploadFile } from "ui-kit/UploadFile";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { FormValues } from "../ReleaseInfo/types";
import { useRelease } from "../hooks/useRelease";
import { Toast } from "primereact/toast"; 
import { runDraftSaveIfNeeded, setDraftSaveGuard } from "../draftSaveGuard";

interface UploadImageProps {
  release: FormValues;
}

export const UploadImage: React.FC<UploadImageProps> = ({ release }) => {
  const { updateRelease } = useRelease();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [cover, setCover] = useState<string | File | null>(null);
  const [relType, setRelType] = useState("");
  const [loading, setLoading] = useState(false);

  const toast = useRef<Toast>(null); 

  const handleRedirect = () => {
    navigate(`/releases/new-release/${id}?step=release-info`);
  };

  const isDirty = useCallback(() => {
    return !!id && cover instanceof File;
  }, [id, cover]);

  const saveDraft = useCallback(async () => {
    if (!id) return;
    if (!(cover instanceof File)) return;
    const form = new FormData();
    form.append("cover", cover);
    setLoading(true);
    try {
      await updateRelease(form);
    } finally {
      setLoading(false);
    }
  }, [id, cover, updateRelease]);

  useEffect(() => {
    setDraftSaveGuard({ isDirty, save: saveDraft });
    return () => setDraftSaveGuard(null);
  }, [isDirty, saveDraft]);

  const onBack = async () => {
    try {
      await runDraftSaveIfNeeded();
    } finally {
    navigate(`/releases/new-release/${id}?step=type`);
    }
  };

  const onNext = async () => {
    if (!id) return;
    if (!cover) return;

    // Only save draft if changed; otherwise navigate immediately.
    if (cover instanceof File) {
      try {
        await saveDraft();
      } catch (error: any) {
        const backendMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Image must be at least 3000x3000 pixels. Please upload a higher resolution cover.";

        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: backendMessage,
          life: 4000,
        });
        return;
      }
    }

    handleRedirect();
  };

  useEffect(() => {
    // Don't clobber user-selected file while they haven't saved yet
    if (cover instanceof File) return;
    setCover(release?.cover_url);
    setRelType(release?.release_type);
  }, [release]);

  // Cover block must be perfectly square on desktop and mobile.
  const uploadWidth = "min(100%)";
  const uploadHeight = uploadWidth;

  return (
    <Container>
      <Toast ref={toast} position="top-right" />

      <Header
        title={
          <FormattedMessage
            id="NewRelease.UploadImage.Title"
            defaultMessage="Cover upload (Step 2/6)"
          />
        }
        breadcrumbsText={
          <FormattedMessage
            id="NewRelease.UploadImage.Breadcrumbs"
            defaultMessage="Release type"
          />
        }
        onBack={onBack}
      />

      <Content>
        <UploadFile
          width={uploadWidth}
          height={uploadHeight}
          square
          accept="image/*,.jpg,.png"
          maxFileSize={6000000}
          setCover={setCover}
          cover={cover}
          release={release}
        >
          <FormattedMessage
            id="NewRelease.UploadImage.Input"
            defaultMessage="Select or drag an image (jpg/png)"
          />
        </UploadFile>

        <Characteristics>
          <Typography variant="body6">
            <FormattedMessage
              id="NewRelease.UploadImage.ContentTitle"
              defaultMessage="Cover Requirements"
            />
          </Typography>

          <Characteristic>
            <Typography variant="body5" color={theme.palette.text.link}>
              <FormattedMessage
                id="NewRelease.UploadImage.Originality"
                defaultMessage="Originality"
              />
            </Typography>
            <Typography variant="body5">
              <FormattedMessage
                id="NewRelease.UploadImage.OriginalityText"
                defaultMessage=": Only use original, non-copyrighted images"
              />
            </Typography>
          </Characteristic>

          <Characteristic>
            <Typography variant="body5" color={theme.palette.text.link}>
              <FormattedMessage
                id="NewRelease.UploadImage.Accuracy"
                defaultMessage="Accuracy"
              />
            </Typography>
            <Typography variant="body5">
              <FormattedMessage
                id="NewRelease.UploadImage.AccuracyText"
                defaultMessage=": The cover should not be misleading. Only include the artist's name, album title, label name, release year, and copyright owner's name"
              />
            </Typography>
          </Characteristic>

          <Characteristic>
            <Typography variant="body5" color={theme.palette.text.link}>
              <FormattedMessage
                id="NewRelease.UploadImage.Quality"
                defaultMessage="Quality"
              />
            </Typography>

            {relType === "music_video" ? (
              <Typography variant="body5">
                <FormattedMessage
                  id="NewRelease.UploadImage.QualityTextMusic"
                  defaultMessage=": JPEG or PNG formats, resolution of at least 2560x1440 pixels, 16:9 ratio (horizontal rectangle), RGB. No blurring or pixelation"
                />
              </Typography>
            ) : (
              <Typography variant="body5">
                <FormattedMessage
                  id="NewRelease.UploadImage.QualityText"
                  defaultMessage="JPEG or PNG formats, without blurring or pixelation"
                />
              </Typography>
            )}
          </Characteristic>

          <Characteristic>
            <Typography variant="body5" color={theme.palette.text.link}>
              <FormattedMessage
                id="NewRelease.UploadImage.MinimumDimensions"
                defaultMessage="Minimum Dimensions"
              />
            </Typography>
            <Typography variant="body5">
              <FormattedMessage
                id="NewRelease.UploadImage.MinimumDimensionsText"
                defaultMessage=": The cover image must be at least 3000 pixels in width and 3000 pixels in height, ensuring high resolution and optimal display across all platforms."
              />
            </Typography>
          </Characteristic>
          <Characteristic>
            <Typography variant="body5" color={theme.palette.text.link}>
              <FormattedMessage
                id="NewRelease.UploadImage.CulturalSensitivity"
                defaultMessage="Cultural Sensitivity"
              />
            </Typography>
            <Typography variant="body5">
              <FormattedMessage
                id="NewRelease.UploadImage.CulturalSensitivityText"
                defaultMessage=": No promotion of hate based on race, religion, gender, or other characteristics"
              />
            </Typography>
          </Characteristic>


          <Characteristic>
            <Typography variant="body5" color={theme.palette.text.link}>
              <FormattedMessage
                id="NewRelease.UploadImage.ProhibitedElements"
                defaultMessage="Prohibited Elements"
              />
            </Typography>
            <Typography variant="body5">
              <FormattedMessage
                id="NewRelease.UploadImage.ProhibitedElementsText"
                defaultMessage=": Links, hashtags, copyrighted logos, pornographic or offensive material, drug promotion, racism, violence, images of celebrities or famous artworks"
              />
            </Typography>
          </Characteristic>
        </Characteristics>
      </Content>

      <BottomBar>
        <Footer
          disabled={!cover}
          nextButtonText={
            <FormattedMessage
              id="NewRelease.UploadImage.Next"
              defaultMessage="Go to release information"
            />
          }
          onNext={onNext}
        />
      </BottomBar>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 38px 20px 30px;
  height: max-content;

  /* Mobile: page itself doesn't scroll; only Content scrolls. Bottom button is always visible */
  @media (max-width: 768px) {
    height: 88dvh;
    overflow: hidden;
  }
`;

const Content = styled.div`
  display: flex;
  gap: 20px;
  padding: 38px 20px 30px;
  height: 100%;

  @media (max-width: 768px) {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex-direction: column;
    align-items: stretch;
    padding: 16px 0 16px;
    gap: 16px;
  }
`;

const Characteristics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 14px;
  }
`;

const Characteristic = styled.span``;

const BottomBar = styled.div`
  @media (max-width: 768px) {
    padding: 12px 0px;
    background: transparent;
  }
`;
